import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { cancelReservationWithCode, requestReservationCancelCode } from "../services/reservationRequest";
import { CancellationReason } from "../models/IReservationRequest";
import { extractApiErrorMessage } from "../utils/apiError";
import { RECAPTCHA_V3_SITE_KEY } from "../config/restaurantConfig";

const CODE_LENGTH = 6;

const GUEST_CANCELLATION_REASONS: { value: CancellationReason; label: string }[] = [
  { value: CancellationReason.PlansChanged, label: "Planlarım değişti" },
  { value: CancellationReason.TimeNotSuitable, label: "Saat bana uygun değil" },
  { value: CancellationReason.FoundAnotherPlace, label: "Başka bir yer buldum" },
  { value: CancellationReason.Other, label: "Diğer" },
];

interface Props {
  reservationId: string;
  onCancelled: () => void;
}

const formatTime = (totalSeconds: number) => {
  const m = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
  const s = Math.floor(totalSeconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

export default function CancelReservation(props: Props) {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_V3_SITE_KEY} language="tr">
      <CancelReservationInner {...props} />
    </GoogleReCaptchaProvider>
  );
}

function CancelReservationInner({ reservationId, onCancelled }: Props) {
  const [step, setStep] = useState<"idle" | "confirm" | "otp">("idle");
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState("");
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [phoneHint, setPhoneHint] = useState("");
  const [expirySeconds, setExpirySeconds] = useState(0);
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (step !== "otp") return;
    const timer = setInterval(() => setExpirySeconds((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [step]);

  const isExpired = expirySeconds <= 0;
  const code = digits.join("");
  const canSubmit = code.length === CODE_LENGTH && !!reason && !isExpired;

  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus();
    inputRefs.current[index]?.select();
  };

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    setError("");
    if (digit && index < CODE_LENGTH - 1) focusInput(index + 1);
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) focusInput(index - 1);
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    const next = Array(CODE_LENGTH).fill("");
    pasted.slice(0, CODE_LENGTH).split("").forEach((char, i) => { next[i] = char; });
    setDigits(next);
    setError("");
    const lastFilled = Math.min(pasted.length, CODE_LENGTH) - 1;
    focusInput(lastFilled >= 0 ? lastFilled : 0);
  };

  const handleRequestCancel = async () => {
    setRequesting(true);
    setError("");
    try {
      const captchaToken = await executeRecaptcha?.("cancel_reservation_code");
      if (!captchaToken) {
        setError("Güvenlik doğrulaması yüklenemedi. Lütfen sayfayı yenileyip tekrar deneyin.");
        return;
      }
      const res = await requestReservationCancelCode(reservationId, captchaToken);
      setPhoneHint(res.data.phoneHint);
      setExpirySeconds((res.data.codeLifetimeMinutes ?? 5) * 60);
      setStep("otp");
      setTimeout(() => focusInput(0), 0);
    } catch (err) {
      setError(extractApiErrorMessage(err, "İptal kodu gönderilirken bir sorun oluştu. Lütfen tekrar deneyin."));
    } finally {
      setRequesting(false);
    }
  };

  const handleSubmitCancel = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await cancelReservationWithCode(reservationId, {
        code,
        cancellationReason: Number(reason) as CancellationReason,
        cancellationNote: note.trim() || undefined,
      });
      onCancelled();
    } catch (err) {
      setError(extractApiErrorMessage(err, "Rezervasyon iptal edilirken bir sorun oluştu. Lütfen tekrar deneyin."));
      setDigits(Array(CODE_LENGTH).fill(""));
      focusInput(0);
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "idle") {
    return (
      <div className="rr-cancel-trigger-wrap">
        {error && (
          <div className="rr-otp-message error">
            <i className="bi bi-exclamation-circle-fill me-1"></i>
            {error}
          </div>
        )}
        <button
          type="button"
          className="rr-btn-outline-danger"
          onClick={() => { setError(""); setStep("confirm"); }}
        >
          <i className="bi bi-x-circle"></i>
          Rezervasyonu İptal Et
        </button>
      </div>
    );
  }

  if (step === "confirm") {
    return (
      <div className="rr-cancel-confirm">
        <p className="rr-cancel-confirm-text">
          <i className="bi bi-exclamation-triangle-fill"></i>
          Rezervasyonu iptal etmek istediğinize emin misiniz?
        </p>
        {error && (
          <div className="rr-otp-message error">
            <i className="bi bi-exclamation-circle-fill me-1"></i>
            {error}
          </div>
        )}
        <div className="rr-cancel-confirm-actions">
          <button
            type="button"
            className="rr-btn-outline-neutral"
            onClick={() => setStep("idle")}
            disabled={requesting}
          >
            Vazgeç
          </button>
          <button
            type="button"
            className="rr-btn-danger"
            onClick={handleRequestCancel}
            disabled={requesting}
          >
            {requesting ? (
              <span className="spinner-border spinner-border-sm" role="status" />
            ) : (
              <i className="bi bi-check2-circle"></i>
            )}
            {requesting ? "Gönderiliyor..." : "Evet, İptal Et"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rr-otp-card">
      <i className="bi bi-x-octagon-fill rr-otp-icon rr-otp-icon-danger"></i>
      <h3 className="rr-otp-title">Rezervasyonu İptal Et</h3>
      <p className="rr-otp-subtitle">
        {phoneHint ? (
          <>
            <strong>{phoneHint}</strong> numarasına gönderilen 6 haneli kodu girin.
          </>
        ) : (
          "Telefonunuza gönderilen 6 haneli kodu girin."
        )}
      </p>

      <div className="rr-otp-inputs" onPaste={handlePaste}>
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            className="rr-otp-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={digit}
            disabled={submitting}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
          />
        ))}
      </div>

      <div className={`rr-otp-timer ${isExpired ? "expired" : ""}`}>
        {isExpired ? (
          <span><i className="bi bi-exclamation-circle-fill me-1"></i>Kodun süresi doldu</span>
        ) : (
          <span><i className="bi bi-clock-fill me-1"></i>Kalan süre: {formatTime(expirySeconds)}</span>
        )}
      </div>

      <div className="rr-form-group full rr-cancel-field">
        <label>İptal Sebebi *</label>
        <select value={reason} onChange={(e) => setReason(e.target.value)} disabled={submitting}>
          <option value="" disabled>Sebep seçin</option>
          {GUEST_CANCELLATION_REASONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      <div className="rr-form-group full rr-cancel-field">
        <label>Not (opsiyonel)</label>
        <textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Eklemek istediğiniz bir not varsa yazabilirsiniz"
          disabled={submitting}
        />
      </div>

      {error && (
        <div className="rr-otp-message error">
          <i className="bi bi-exclamation-circle-fill me-1"></i>
          {error}
        </div>
      )}

      <button
        type="button"
        className="rr-btn-danger rr-otp-verify-btn"
        disabled={!canSubmit || submitting}
        onClick={handleSubmitCancel}
      >
        {submitting ? (
          <span className="spinner-border spinner-border-sm" role="status" />
        ) : (
          <i className="bi bi-check2-circle"></i>
        )}
        {submitting ? "İptal Ediliyor..." : "Rezervasyonu İptal Et"}
      </button>

      <button type="button" className="rr-otp-cancel" onClick={() => setStep("idle")} disabled={submitting}>
        <i className="bi bi-arrow-left"></i> Vazgeç
      </button>
    </div>
  );
}
